import React from 'react'
import styled from 'styled-components'
import { format } from 'date-fns'
import Dialog from '@mui/material/Dialog'
import CircularProgress from '@mui/material/CircularProgress'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { Grid } from '@mui/material'
import Typography from '@/v2/components/common/Typography'
import Button from '@/v2/components/common/Button'

const Wrapper = styled.div`
  padding: 38px 30px 23px 30px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 280px;
  text-align: center;
`

// Soft amber banner mirroring MUI's "warning" look, but as a plain styled box so
// it renders consistently here (the themed MUI Alert lost its background inside
// this dialog). Black text keeps the message readable on the light fill.
const WarningBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
  background: #fff4e5;
  color: #000;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.4;

  svg {
    flex-shrink: 0;
    color: #ed6c02;
  }
`

const Times = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`

const formatTime = date => (date ? format(date, 'h:mm aa') : '')

const MoveAppointmentDialog = ({
  isOpen,
  loading = false,
  patientName,
  fromTime,
  toTime,
  fromColumnTitle,
  toColumnTitle,
  warning,
  onConfirm,
  onCancel,
}) => {
  // A drag can move an appointment sideways as well as up and down, so name the
  // destination chair whenever it changed — the times alone wouldn't show it.
  const movedColumn = !!toColumnTitle
  // A sideways-only drag keeps the time, so the before/after times would read as
  // an identical pair — drop them and show just the room change.
  const sameTime =
    movedColumn && fromTime && toTime && fromTime.getTime() === toTime.getTime()
  const destination = movedColumn
    ? sameTime
      ? 'a new room'
      : 'a new time and room'
    : 'a new time'

  return (
    <Dialog open={isOpen} onClose={loading ? undefined : onCancel}>
      <Wrapper>
        <Typography variant="h3">Move this appointment?</Typography>
        <Typography variant="h5" fontWeight="400">
          {patientName ? `${patientName}'s appointment` : 'This appointment'}{' '}
          will be moved to {destination}.
        </Typography>
        {warning && (
          <WarningBox>
            <WarningAmberRoundedIcon fontSize="small" />
            {warning}
          </WarningBox>
        )}
        {!sameTime && (
          <Times>
            <Typography variant="h4" fontWeight="500">
              {formatTime(fromTime)}
            </Typography>
            <Typography variant="h4" fontWeight="400">
              →
            </Typography>
            <Typography variant="h4" fontWeight="600">
              {formatTime(toTime)}
            </Typography>
          </Times>
        )}
        {movedColumn && (
          <Times>
            <Typography variant="h5" fontWeight="500">
              {fromColumnTitle}
            </Typography>
            <Typography variant="h5" fontWeight="400">
              →
            </Typography>
            <Typography variant="h5" fontWeight="600">
              {toColumnTitle}
            </Typography>
          </Times>
        )}
        <Grid display="flex" width="100%" justifyContent="flex-end" gap={1}>
          <Button
            variant="text"
            color="secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onConfirm}
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            Confirm
          </Button>
        </Grid>
      </Wrapper>
    </Dialog>
  )
}

export default MoveAppointmentDialog
